import { Web3DViewer } from '@technology/web3d';
import {
  LineMarkup,
  Markup,
  MarkupEventName,
} from '@technology/web3d-plugin-markup';
import { Accessor, createEffect, on } from 'solid-js';
import { CapabilityCoordinatorBase } from './capability-coordinator-base';
import { CapabilityRegistryContainer } from './capability-registry/capability-registry-container';
import { Web3DViewerWrapper } from '@trimble/utilities';
import { ANNOTATION_MARKUP_NAMES } from '@trimble/web3d-providers';

/**
 * Sets `markup.editable` and, for line-based markups (arrow/line), also
 * toggles `userData.isMovable` on both handles. This is needed because
 * `ArrowMarkup.showHandles` always forces `startHandle.visible = true`,
 * bypassing the `editable` guard in `LineMarkup.showHandles`. Setting
 * `isMovable = false` keeps handles visible (so the markup is still
 * selectable) but prevents the drag subscription from firing.
 */
function applyEditableState(markup: Markup, editable: boolean): void {
  if (markup instanceof LineMarkup && !markup.startHandle) {
    return;
  }
  if (markup.editable !== editable) {
    markup.editable = editable;
  }
  if (markup instanceof LineMarkup) {
    if (
      markup.startHandle &&
      markup.startHandle.userData['isMovable'] !== editable
    ) {
      markup.startHandle.userData['isMovable'] = editable;
    }
    if (
      markup.endHandle &&
      markup.endHandle.userData['isMovable'] !== editable
    ) {
      markup.endHandle.userData['isMovable'] = editable;
    }
  }
}

export class Hide2DMarkupCapabilityCoordinator extends CapabilityCoordinatorBase<CapabilityRegistryContainer> {
  private readonly viewer: Web3DViewer;
  private readonly markupModifiedHandler: (e: Event) => void;
  private readonly visibilityChangedHandler: () => void;

  constructor(
    registryContainer: CapabilityRegistryContainer,
    viewerWrapper: Web3DViewerWrapper,
    allowMarkup: Accessor<boolean>,
    allowMarkupEditing: Accessor<boolean> = () => true
  ) {
    super(registryContainer);

    this.viewer = viewerWrapper.viewer;

    this.markupModifiedHandler = (e: Event) => {
      const markup = (e as CustomEvent<Markup>).detail;
      if (!markup) {
        return;
      }
      const desiredVisible = allowMarkup();
      if (markup.visible !== desiredVisible) {
        markup.visible = desiredVisible;
      }
      if (ANNOTATION_MARKUP_NAMES.has(markup.name)) {
        applyEditableState(markup, allowMarkupEditing());
      }
    };

    this.visibilityChangedHandler = () => {
      if (!allowMarkup()) {
        viewerWrapper.markupPlugin.getMarkups().forEach((markup) => {
          if (markup && markup.visible !== false) {
            markup.visible = false;
          }
        });
      }
    };

    this.viewer.addEventListener(
      'markupmodified' as MarkupEventName.Modified,
      this.markupModifiedHandler
    );

    this.viewer.addEventListener(
      'markupvisibilitychanged',
      this.visibilityChangedHandler
    );

    createEffect(
      on(allowMarkup, () => {
        const val = allowMarkup();
        viewerWrapper.markupPlugin.getMarkups().forEach((markup) => {
          if (markup && markup.visible !== val) {
            markup.visible = val;
          }
        });
      })
    );

    createEffect(
      on(allowMarkupEditing, () => {
        const val = allowMarkupEditing();
        viewerWrapper.markupPlugin.getMarkups().forEach((markup) => {
          if (markup && ANNOTATION_MARKUP_NAMES.has(markup.name)) {
            applyEditableState(markup, val);
          }
        });
      })
    );
  }

  public async cleanup(): Promise<void> {
    this.viewer.removeEventListener(
      'markupmodified' as MarkupEventName.Modified,
      this.markupModifiedHandler
    );
    this.viewer.removeEventListener(
      'markupvisibilitychanged',
      this.visibilityChangedHandler
    );
  }
}
